import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { formatCopy } from '@/i18n/ns';
import type { Formatters } from './format';
import type { UsageCopy } from './copy';

const MAX_LIMIT = 10_000_000;

function digits(value: string) {
  let out = '';
  for (const char of value) if (char >= '0' && char <= '9' && out.length < 8) out += char;
  return out;
}

function help(copy: UsageCopy, f: Formatters, name: string, used: number, draft: string) {
  const value = draft === '' ? NaN : Number(draft);
  const valid = Number.isSafeInteger(value) && value >= 1 && value <= MAX_LIMIT;
  if (draft !== '' && !valid) return { text: copy.limitInvalid, warn: true, valid };
  if (valid && value <= used)
    return {
      text: formatCopy(copy.limitPausesNow, { name, used: f.int(used) }),
      warn: true,
      valid,
    };
  if (valid)
    return {
      text: formatCopy(copy.limitHelpValid, {
        name,
        used: f.int(used),
        notice: f.int(Math.ceil(value * 0.8)),
        limit: f.int(value),
      }),
      warn: false,
      valid,
    };
  return { text: formatCopy(copy.limitHelp, { name }), warn: false, valid };
}

/** Inline monthly limit editor for one person (owner only). */
export function LimitEditor({
  name,
  usedCredits,
  limitCredits,
  copy,
  f,
  onSave,
  onCancel,
}: {
  name: string;
  usedCredits: number;
  limitCredits: number | null;
  copy: UsageCopy;
  f: Formatters;
  onSave: (credits: number | null) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(limitCredits ? String(Math.round(limitCredits)) : '');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const field = input.current;
    field?.focus?.({ preventScroll: true });
    field?.select?.();
  }, []);
  const state = help(copy, f, name, usedCredits, draft);
  const save = async (value: number | null) => {
    if (busy || (value !== null && !state.valid)) return;
    setBusy(true);
    const done = await onSave(value);
    if (!done) setBusy(false);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void save(Number(draft));
    } else if (event.key === 'Escape') onCancel();
  };
  return (
    <div className="cu-editor" onClick={(event) => event.stopPropagation()}>
      <label className="cu-editor-field">
        <span className="cu-editor-label">{formatCopy(copy.limitFor, { name })}</span>
        <span className={`cu-input-wrap${draft !== '' && !state.valid ? ' is-invalid' : ''}`}>
          <input
            ref={input}
            className="cu-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={draft}
            aria-invalid={draft !== '' && !state.valid}
            onChange={(event) => setDraft(digits(event.target.value))}
            onInput={(event) => setDraft(digits((event.target as HTMLInputElement).value))}
            onKeyDown={onKeyDown}
          />
          <span className="cu-input-suffix">{copy.creditsPerMonth}</span>
        </span>
      </label>
      <p className={`cu-editor-help${state.warn ? ' is-warn' : ''}`} aria-live="polite">
        {state.text}
      </p>
      <div className="cu-editor-actions">
        <button
          type="button"
          className="cu-btn is-primary"
          disabled={!state.valid || busy}
          onClick={() => void save(Number(draft))}
        >
          {busy ? copy.saving : copy.save}
        </button>
        {limitCredits !== null && (
          <button
            type="button"
            className="cu-btn is-danger"
            disabled={busy}
            onClick={() => void save(null)}
          >
            {copy.removeLimit}
          </button>
        )}
        <button type="button" className="cu-btn" onClick={onCancel}>
          {copy.cancel}
        </button>
      </div>
    </div>
  );
}
